import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermission,
  requiredPermissions,
  requireAll = false
}) => {
  const { currentUser, dbUser, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="textSecondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser || !dbUser) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is active
  if (dbUser.is_active !== 'true' && dbUser.is_active !== 'active') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Account Inactive
        </Typography>
        <Typography variant="body2">
          Your account has been deactivated. Please contact your administrator.
        </Typography>
      </Box>
    );
  }

  // Helper function to parse permissions
  const parsePermissions = (permissionString: string): string[] => {
    if (!permissionString) return [];
    return permissionString.split(',').map(p => p.trim()).filter(p => p);
  };

  // Helper function to check if user has a specific permission (direct matching)
  const hasPermission = (userPermissions: string[], requiredPermission: string): boolean => {
    return userPermissions.includes(requiredPermission);
  };

  // Check single permission
  if (requiredPermission) {
    const userPermissions = parsePermissions(dbUser.permission);
    if (!hasPermission(userPermissions, requiredPermission)) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have permission to access this resource.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Required: {requiredPermission}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Your permissions: {userPermissions.join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = parsePermissions(dbUser.permission);
    const hasAccess = requireAll 
      ? requiredPermissions.every(permission => hasPermission(userPermissions, permission))
      : requiredPermissions.some(permission => hasPermission(userPermissions, permission));
      
    if (!hasAccess) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have permission to access this resource.
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Required: {requireAll ? 'All of' : 'Any of'} [{requiredPermissions.join(', ')}]
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Your permissions: {userPermissions.join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
